import React from 'react';
import { Box, Flex, Text } from 'theme-ui';
import useProducts from '~/hooks/graphql/queries/use-products';
import useProductMainImage from '~/hooks/components/use-product-main-image';
import CartQuantitySelector from '~/components/Cart/QuantitySelector';
import { EnhancedBundleLineItem } from '~/utils/use-cart-items';
import LineItemGeneric from './LineItemGeneric';
import ShippingNote from '~/components/Generic/ShippingNote';

type Props = {
  item: EnhancedBundleLineItem;
  confirmed?: boolean;
};

const KitLineItem = ({ item, confirmed = false }: Props) => {
  // separate kit into the products that make it up and render each lineitem individually underneath it.
  const { displayAmount, quantity, id } = item;

  const priceWithoutDecimal = displayAmount.split('.')[0];

  // if using pipe sku variants, split up sku
  const variantSkuArray = item.variant.sku.split('|');

  const products = useProducts();

  const getProductsArray = (variantSkuArray: string[], products: Queries.ProductBaseFragment[]): KitProduct[] => {
    return variantSkuArray.reduce<KitProduct[]>((acc, variantSku) => {
      const product = products.find((p) => p?.variants?.find((variant) => variant?.sku === variantSku));
      const variant = product?.variants?.find((v) => v?.sku === variantSku);

      if (!product || !variant) {
        console.error(`Product or variant not found for SKU: ${variantSku}. Skipping item.`);
        return acc;
      }

      return [
        ...acc,
        {
          ...product,
          variant: { ...variant, productName: product?.name },
          sku: variantSku,
          id: product?.id ?? '',
        },
      ];
    }, []);
  };

  const productsArray = getProductsArray(variantSkuArray, products);

  // Don't render anything if no products were found
  if (productsArray.length === 0) {
    console.error(`No valid products found for kit: ${item.variant.name}`);
    return null;
  }

  // Find out whether or not to render the shipping notes.
  const kitHasShippingNotes = productsArray.some((product) => product.variant.optionalShippingNote);

  return (
    <Box
      sx={{
        background: 'white',
        width: '100%',
        borderRadius: '1.6rem 0 1.6rem 0',
        overflow: 'hidden',
        WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        '> .lineItemKit:not(:last-child)': {
          borderBottom: '1px solid',
          borderColor: 'cloud',
        },
      }}
    >
      <Flex
        sx={(t) => ({
          width: '100%',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${t.colors?.cloud}`,
          height: '6.4rem',
          px: '1.6rem',
          alignItems: 'center',
          alignContent: 'center',
        })}
      >
        <Text
          as="p"
          sx={{
            width: '20rem',
            fontWeight: 'medium',
            fontSize: '1.6rem',
            lineHeight: '100%',
            textTransform: 'uppercase',
            color: 'black',
          }}
          dangerouslySetInnerHTML={{ __html: item.variant.name }}
        ></Text>
        <Flex sx={{ gap: '1.6rem' }}>
          <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
            <Text
              sx={{
                fontSize: '1rem',
                textTransform: 'uppercase',
                fontWeight: '400',
                lineHeight: '100%',
                color: 'UIGray2',
              }}
            >
              Quantity
            </Text>
            <Text
              sx={{
                color: 'black',
                fontSize: '1.2rem',
                textTransform: 'uppercase',
                lineHeight: '1.2rem',
                height: '1.8rem',
                fontWeight: 'medium',
              }}
              as="p"
            >
              {confirmed ? (
                quantity
              ) : (
                <CartQuantitySelector
                  forwardSx={{ p: 0, m: 0, lineHeight: 0 }}
                  aria-label="quantity selector"
                  label="quantity"
                  quantity={quantity}
                  lineItemId={id}
                />
              )}
            </Text>
          </Flex>
          <Flex sx={{ flexDirection: 'column', justifyContent: 'start', gap: '8px' }}>
            <Text
              sx={{
                fontSize: '1rem',
                textTransform: 'uppercase',
                fontWeight: '400',
                lineHeight: '100%',
                color: 'UIGray2',
              }}
            >
              Price
            </Text>
            <Text
              sx={{
                fontSize: '1.2rem',
                color: 'black',
                textTransform: 'uppercase',
                lineHeight: '1.2rem',
                fontWeight: 'medium',
              }}
              as="p"
            >
              {priceWithoutDecimal}
            </Text>
          </Flex>
        </Flex>
      </Flex>

      {productsArray.map((product) => (
        <div className="lineItemKit" key={product.id}>
          <IndividualProduct kit product={product} />
        </div>
      ))}
      {kitHasShippingNotes && (
        <Flex
          sx={{
            minHeight: '6.4rem',
            p: '1.6rem',
            gap: '1rem',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'center',
          }}
        >
          {productsArray.map(
            (product) =>
              product.variant.optionalShippingNote && (
                <ShippingNote key={product.id} note={`${product.name}: ${product.variant.optionalShippingNote}`} />
              )
          )}
        </Flex>
      )}
    </Box>
  );
};

type KitProduct = Queries.ProductBaseFragment & {
  sku: string;
  variant: Queries.VariantBaseFragment & { productName: string | null | undefined };
  id: string;
};

// Split products in kit lineItem and repackage
const IndividualProduct = ({ product, kit }: { product: KitProduct; kit: boolean }) => {
  const { image } = useProductMainImage({ slug: product.slug ?? '', sku: product.sku ?? '' });

  const {
    variant: { optionValues },
  } = product;

  const color = optionValues?.find((option) => /color/i.test(option?.slug ?? ''))?.presentation;

  const size = optionValues?.find((option) => /size/i.test(option?.slug ?? ''))?.presentation;

  return (
    <LineItemGeneric
      image={image}
      kit={kit}
      title={product.name ?? ''}
      size={size ?? ''}
      color={color ?? ''}
      quantity={1}
      id={product.variant.id ?? 0}
    />
  );
};

export default KitLineItem;
